// 슬래시(/) 메뉴 아이템 — 기본 항목 + 커스텀 블록(수식/북마크/동기화 블록/임베드/콜아웃).
//
// BlockNote 0.51.4 API (node_modules 실제 export 로 검증):
// - @blocknote/react: SuggestionMenuController, getDefaultReactSlashMenuItems
// - @blocknote/core:  filterSuggestionItems, insertOrUpdateBlockForSlashMenu
//   (types/src/extensions/SuggestionMenu/getDefaultSlashMenuItems.d.ts)
// 아이템 shape: { title, onItemClick, subtext?, aliases?, group?, icon? } (DefaultReactSuggestionItem)

import { filterSuggestionItems, insertOrUpdateBlockForSlashMenu } from '@blocknote/core';
import { getDefaultReactSlashMenuItems } from '@blocknote/react';
import { HAS_COLUMNS } from './schema';

const CUSTOM_GROUP = '루트 블록';

// 멀티 컬럼(xl-multi-column) 삽입 페이로드 — 패키지의 getMultiColumnSlashMenuItems 와 동일한
// 블록 구조(columnList > column×N > paragraph). 패키지 항목 대신 직접 정의하는 이유:
// 패키지 항목은 editor.dictionary.multi_column(로케일 사전 머지)을 요구하는데, 우리는
// 기본 dictionary 를 쓰므로 기존 커스텀 항목과 같은 패턴(한국어 제목 + alias)으로 맞춘다.
function columnListPayload(n) {
  return {
    type: 'columnList',
    children: Array.from({ length: n }, () => ({
      type: 'column',
      children: [{ type: 'paragraph' }],
    })),
  };
}

// 멀티 컬럼 삽입 항목 — 스키마에 columnList/column 이 등록된 경우에만 노출.
function getColumnSlashMenuItems(editor) {
  if (!HAS_COLUMNS) return [];
  return [
    {
      title: '2개 열',
      subtext: '두 열을 나란히 배치',
      aliases: ['열', '컬럼', '단', '2열', 'columns', 'column', 'two columns', 'split', 'row'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>▥</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, columnListPayload(2));
      },
    },
    {
      title: '3개 열',
      subtext: '세 열을 나란히 배치',
      aliases: ['열', '컬럼', '단', '3열', 'columns', 'column', 'three columns', 'split', 'row'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>▦</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, columnListPayload(3));
      },
    },
  ];
}

// 커스텀 블록 삽입 항목 — 한국어 제목 + 영문 alias(검색용).
export function getCustomSlashMenuItems(editor) {
  return [
    {
      title: '수식',
      subtext: 'LaTeX 수학 수식 (KaTeX)',
      aliases: ['수식', '수학', 'equation', 'math', 'latex', 'katex', 'formula'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 18 }}>∑</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, { type: 'equation' });
      },
    },
    {
      title: '북마크',
      subtext: '웹 링크를 카드로 표시',
      aliases: ['북마크', '링크', 'bookmark', 'link', 'web'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>🔖</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, { type: 'bookmark' });
      },
    },
    {
      title: '동기화 블록',
      subtext: '여러 페이지에서 함께 수정되는 블록',
      aliases: ['동기화', '동기화블록', 'synced', 'sync', 'syncedblock', 'synced block'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>🔁</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, { type: 'syncedBlock' });
      },
    },
    {
      title: '임베드',
      subtext: 'YouTube · Vimeo · 지도 · PDF 등 iframe 임베드',
      aliases: ['임베드', '아이프레임', 'embed', 'iframe', 'youtube', 'vimeo', 'maps', 'pdf', 'video'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>🌐</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, { type: 'embed' });
      },
    },
    {
      title: '콜아웃',
      subtext: '아이콘과 배경이 있는 강조 상자',
      aliases: ['콜아웃', '강조', 'callout', 'highlight', 'note'],
      group: CUSTOM_GROUP,
      icon: <span style={{ fontSize: 16 }}>💡</span>,
      onItemClick: () => {
        insertOrUpdateBlockForSlashMenu(editor, { type: 'callout' });
      },
    },
  ];
}

// SuggestionMenuController.getItems 로 넘길 통합 함수 — 기본 항목 + 커스텀 항목을 query 로 필터.
export async function getRootSlashMenuItems(editor, query) {
  return filterSuggestionItems(
    [
      ...getDefaultReactSlashMenuItems(editor),
      ...getColumnSlashMenuItems(editor),
      ...getCustomSlashMenuItems(editor),
    ],
    query,
  );
}
